import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React from 'react';
import Layout from '../../../common/components/Layout';
import { SimpleBreadcrumbs } from '../../../common/components/SimpleBreadcrumbs';
import { UserRestriction } from '../../../common/components/UserRestiction';
import { getProject } from '../../../common/services/project';
import { BroadcastAffiliationEditForm } from '../Detail/BroadcastAffiliationEditForm.component';
import { BroadcastAffiliationScope, IBroadcastAffiliation, IBroadcastAffiliationTarget } from '../typings';
import { IComponentProps } from './BroadcastAffiliationList.connector';

// Outlined chip colors per target kind (no red: red is reserved for the inactive status).
const TARGET_CHIP_STYLES: Record<BroadcastAffiliationScope, React.CSSProperties> = {
  match: { borderColor: '#1976d2', color: '#1976d2' },
  team: { borderColor: '#f57c00', color: '#e65100' },
  competition: { borderColor: '#9e9e9e', color: '#616161' },
  channel: { borderColor: '#bdbdbd', color: '#757575', backgroundColor: '#f5f5f5' },
};

const STATUS_COLORS = { active: '#4caf50', inactive: '#f44336' };

export class BroadcastAffiliationList extends React.Component<IComponentProps> {
  public componentDidMount() {
    this.fetchData();
  }

  public render() {
    const {
      broadcastAffiliations,
      fetching,
      intl: { messages: m },
    } = this.props;

    const hasItems = broadcastAffiliations && broadcastAffiliations.length > 0;

    return (
      <Layout
        id="broadcast-affiliations-layout"
        top={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <SimpleBreadcrumbs crumbStack={[{ labelKey: 'app_broadcastAffiliations' }]} />
            <UserRestriction>
              <Button
                variant="contained"
                color="primary"
                onClick={() => this.props.history.push(`${getProject().url}/broadcast-affiliations/add`)}
              >
                <AddIcon style={{ marginRight: 8 }} />
                {m.button_add}
              </Button>
            </UserRestriction>
          </div>
        }
        main={
          fetching && !hasItems
            ? <CircularProgress size={80} style={{ margin: 'auto' }} />
            : (
              <>
                {this.renderLegend()}
                {this.sortAffiliations(broadcastAffiliations || []).map((affiliation) => this.renderAccordion(affiliation))}
              </>
            )
        }
      />
    );
  }

  private renderAccordion = (affiliation: IBroadcastAffiliation) => {
    const {
      intl: { messages: m },
    } = this.props;

    const channelName = affiliation.channel ? affiliation.channel.name : '-';
    const displayLabel = affiliation.useLabel && affiliation.label ? affiliation.label : channelName;
    const targets = affiliation.targets || [];

    return (
      <Accordion key={affiliation.id} TransitionProps={{ unmountOnExit: true }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
            <Typography variant="subtitle1" style={{ minWidth: 220 }}>
              <strong>{channelName}</strong>
              {affiliation.useLabel && affiliation.label ? ` — ${displayLabel}` : ''}
            </Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flex: 1 }}>
              {targets.length === 0 ? (
                <Chip size="small" variant="outlined" label={m.field_channel} />
              ) : (
                targets.map((target, index) => this.renderTargetChip(target, index))
              )}
            </div>
            {this.renderStatusChip(affiliation)}
            <Typography variant="caption" color="textSecondary" title={affiliation.id}>
              {m.field_affiliationId} : {affiliation.id}
            </Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails style={{ display: 'block' }}>
          <BroadcastAffiliationEditForm
            affiliation={affiliation}
            onSave={(payload) => this.handleSave(affiliation.id, payload)}
            onDelete={this.handleDelete}
          />
        </AccordionDetails>
      </Accordion>
    );
  };

  private handleSave = (id: string, payload: object) => {
    // @ts-ignore - thunk returns a promise
    this.props.updateBroadcastAffiliation(id, payload).then(() => this.fetchData());
  };

  private handleDelete = (id: string) => {
    // @ts-ignore - thunk returns a promise
    this.props.deleteBroadcastAffiliation(id).then(() => this.fetchData());
  };

  // Enabled first, then by start date (affiliations without start date — always on — first, then chronological).
  private sortAffiliations = (affiliations: IBroadcastAffiliation[]): IBroadcastAffiliation[] =>
    [...affiliations].sort((a, b) => {
      if (a.isEnabled !== b.isEnabled) return a.isEnabled ? -1 : 1;
      const startA = a.startDate ? new Date(a.startDate).getTime() : -Infinity;
      const startB = b.startDate ? new Date(b.startDate).getTime() : -Infinity;
      return startA - startB;
    });

  private formatDate = (date?: string | null) => (date ? new Date(date).toLocaleDateString() : '…');

  // Active = enabled and inside the schedule window (start/end dates), evaluated now.
  // The window is shown on a second line inside the chip.
  private renderStatusChip = (affiliation: IBroadcastAffiliation) => {
    const {
      intl: { messages: m },
    } = this.props;
    const now = Date.now();
    const started = !affiliation.startDate || new Date(affiliation.startDate).getTime() <= now;
    const notEnded = !affiliation.endDate || new Date(affiliation.endDate).getTime() >= now;
    const active = affiliation.isEnabled && started && notEnded;
    const hasWindow = !!(affiliation.startDate || affiliation.endDate);

    return (
      <Chip
        size="small"
        icon={active ? <CheckIcon style={{ color: '#fff' }} /> : <CloseIcon style={{ color: '#fff' }} />}
        label={
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, padding: hasWindow ? '3px 0' : 0 }}>
            <span>{active ? m.field_active : m.field_inactive}</span>
            {hasWindow && (
              <span style={{ fontSize: 10, opacity: 0.9 }}>
                {this.formatDate(affiliation.startDate)} → {this.formatDate(affiliation.endDate)}
              </span>
            )}
          </div>
        }
        style={{
          backgroundColor: active ? STATUS_COLORS.active : STATUS_COLORS.inactive,
          color: '#fff',
          height: 'auto',
          minHeight: 24,
        }}
      />
    );
  };

  // One chip per target: match "Home vs Away · date", team "Team (Competition)", competition name.
  // Colors are neutral (blue / orange / grey) so they do not read as an error state next to the status chip.
  private renderTargetChip = (target: IBroadcastAffiliationTarget, index: number) => {
    const {
      intl: { messages: m },
    } = this.props;
    const competitionName = target.competition ? (target.competition as any).shortName || target.competition.name : null;

    let label: string;
    let kind: BroadcastAffiliationScope;
    if (target.match) {
      const match = target.match as any;
      const description = match.description || `${match.homeTeam ? match.homeTeam.name : '?'} vs ${match.awayTeam ? match.awayTeam.name : '?'}`;
      label = `${description} · ${this.formatDate(match.date)}`;
      kind = 'match';
    } else if (target.team) {
      label = competitionName ? `${target.team.name} (${competitionName})` : target.team.name;
      kind = 'team';
    } else if (target.competition) {
      label = competitionName || (m.field_competition as string);
      kind = 'competition';
    } else {
      label = m.field_channel as string;
      kind = 'channel';
    }

    return <Chip key={target.id || index} size="small" variant="outlined" label={label} style={TARGET_CHIP_STYLES[kind]} />;
  };

  // Explains the target chip colors and the status chip.
  private renderLegend = () => {
    const {
      intl: { messages: m },
    } = this.props;
    const sample = (kind: BroadcastAffiliationScope, label: string) => (
      <Chip size="small" variant="outlined" label={label} style={{ ...TARGET_CHIP_STYLES[kind], marginRight: 8 }} />
    );
    const row = (chip: React.ReactNode, text: React.ReactNode) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
        {chip}
        <Typography variant="caption">{text}</Typography>
      </div>
    );

    return (
      <Paper variant="outlined" style={{ padding: '8px 16px', marginBottom: 12 }}>
        <Typography variant="subtitle2" gutterBottom>
          {m.legend_title}
        </Typography>
        {row(sample('match', m.field_match as string), m.legend_target_match)}
        {row(sample('team', m.field_team as string), m.legend_target_team)}
        {row(sample('competition', m.field_competition as string), m.legend_target_competition)}
        {row(sample('channel', m.field_channel as string), m.legend_target_channel)}
        {row(
          <>
            <Chip size="small" label={m.field_active} style={{ backgroundColor: STATUS_COLORS.active, color: '#fff', marginRight: 4 }} />
            <Chip size="small" label={m.field_inactive} style={{ backgroundColor: STATUS_COLORS.inactive, color: '#fff', marginRight: 8 }} />
          </>,
          m.legend_status,
        )}
      </Paper>
    );
  };

  private fetchData = () => {
    this.props.fetchBroadcastAffiliations();
  };
}
